// src/AppRoutes.jsx — full replacement
import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';

// Auth screens
import BootSpinner    from './auth/screens/BootSpinner.jsx';
import LoginScreen    from './auth/screens/LoginScreen.jsx';
import TenantPicker   from './auth/screens/TenantPicker.jsx';
import NoAccessScreen from './auth/screens/NoAccessScreen.jsx';

// Shell + sections
import AppShell         from './shell/AppShell.jsx';
import Dashboard        from './sections/Dashboard.jsx';
import Credits          from './sections/Credits.jsx';
import ContentGenerator from './sections/ContentGenerator.jsx';
import SeoRewrite       from './sections/SeoRewrite.jsx';

// Admin
import AdminLayout           from './admin/AdminLayout.jsx';
import AdminOverviewPage     from './admin/pages/AdminOverviewPage.jsx';
import UserListPage          from './admin/pages/UserListPage.jsx';
import UserDetailPage        from './admin/pages/UserDetailPage.jsx';
import TenantManagementPage  from './admin/pages/TenantManagementPage.jsx';
import PlanListPage          from './admin/pages/PlanListPage.jsx';
import RunListPage           from './admin/pages/RunListPage.jsx';
import RunDetailPage         from './admin/pages/RunDetailPage.jsx';

// Tenant-admin
import TenantUserListPage    from './tenantAdmin/pages/TenantUserListPage.jsx';
import TenantRunListPage     from './tenantAdmin/pages/TenantRunListPage.jsx';

function RequireSuperadmin({ children }) {
  const { isSuperadmin } = useAuth();
  if (!isSuperadmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireTenantAdmin({ children }) {
  const { isTenantAdmin, activeTenant, isSuperadmin, adminMode } = useAuth();
  // Superadmins always route through /admin/* — never the tenant-admin console.
  if (isSuperadmin) return <Navigate to={adminMode ? '/admin/users' : '/dashboard'} replace />;
  if (!isTenantAdmin || !activeTenant) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function AppRoutes() {
  const { phase } = useAuth();

  if (phase === 'booting') return <BootSpinner />;

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login"         element={<LoginScreen />} />
      <Route path="/select-tenant" element={<TenantPicker />} />
      <Route path="/no-access"     element={<NoAccessScreen />} />

      {/* Protected routes — AppShell redirects if not authenticated */}
      <Route element={<AppShell />}>
        <Route index                     element={<Navigate to="/content-generator" replace />} />
        <Route path="/dashboard"         element={<Dashboard />} />
        <Route path="/credits"           element={<Credits />} />
        <Route path="/content-generator" element={<ContentGenerator />} />
        <Route path="/seo-rewrite"       element={<SeoRewrite />} />

        {/* Run history — available to every tenant member (no admin guard).
            BE /pipeline/runs is tenant-scoped (not admin-only) and nulls creator
            PII for non-admins, so members see the full tenant set safely. */}
        <Route path="/runs"          element={<TenantRunListPage />} />
        <Route path="/runs/:taskId"  element={<RunDetailPage />} />

        {/* Admin routes — defense-in-depth gate; BE 403 still backs this */}
        <Route path="/admin" element={<RequireSuperadmin><AdminLayout /></RequireSuperadmin>}>
          <Route index element={<Navigate to="/admin/overview" replace />} />
          <Route path="overview" element={<AdminOverviewPage />} />
          <Route path="users" element={<UserListPage />} />
          <Route path="users/:userId" element={<UserDetailPage />} />
          <Route path="tenants" element={<TenantManagementPage />} />
          <Route path="plans" element={<PlanListPage />} />
          <Route path="runs" element={<RunListPage />} />
          <Route path="runs/:taskId" element={<RunDetailPage />} />
        </Route>

        {/* Tenant-admin routes — gated by RequireTenantAdmin */}
        <Route path="/tenant-admin" element={<RequireTenantAdmin><Outlet /></RequireTenantAdmin>}>
          <Route index element={<Navigate to="/tenant-admin/users" replace />} />
          <Route path="users" element={<TenantUserListPage />} />
          <Route path="runs" element={<TenantRunListPage />} />
          <Route path="runs/:taskId" element={<RunDetailPage />} />
        </Route>
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
